'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import type { AreaType } from '@/types/area-type'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

interface ResponsiveAreasProps {
  areas: AreaType[]
}

export default function ResponsiveAreas({ areas }: ResponsiveAreasProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const params = new URLSearchParams(searchParams.toString())
  const areaQuery = params.get('area')

  const defaultValue = areaQuery ?? 'mais-procurados'

  function handleSelectedChange(value: string) {
    if (value === 'mais-procurados') {
      router.push('/', { scroll: false })
      return
    }

    params.set('area', value)
    router.push(`?${params.toString()}`, { scroll: false })
  }

  return (
    <div className="w-full">
      <Tabs
        className="w-full"
        onValueChange={handleSelectedChange}
        value={defaultValue}
      >
        <div className="hidden md:block">
          <TabsList
            className={cn(
              'relative flex w-full items-center rounded-none border-primary-100 border-b-[3px] bg-transparent px-0 py-0',
              'scrollbar-hide overflow-x-auto scroll-smooth',
              'snap-x snap-mandatory'
            )}
          >
            <TabsTrigger
              className={cn(
                'group relative flex-shrink-0 whitespace-nowrap px-3 py-2 font-medium text-gray-500 text-sm transition-colors',
                'hover:text-primary-700 data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-primary-700',
                'snap-start'
              )}
              value="mais-procurados"
            >
              Mais Procurados
              <span
                aria-hidden
                className="-bottom-[3px] absolute left-0 h-[3px] w-full origin-left rounded-full bg-primary-500 opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100 group-data-[state=active]:opacity-100"
              />
            </TabsTrigger>

            {areas.map(option => (
              <TabsTrigger
                className={cn(
                  'group relative flex-shrink-0 whitespace-nowrap px-3 py-2 font-medium text-gray-500 text-sm transition-colors',
                  'hover:text-primary-700 data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-primary-700',
                  'snap-start'
                )}
                key={option.reference}
                value={option.url}
              >
                {option.name}
                <span
                  aria-hidden
                  className="-bottom-[3px] absolute left-0 h-[3px] w-full origin-left rounded-full bg-primary-500 opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100 group-data-[state=active]:opacity-100"
                />
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="block md:hidden">
          <Select onValueChange={handleSelectedChange} value={defaultValue}>
            <SelectTrigger className="w-full rounded-md bg-transparent px-4 py-6 text-left">
              <SelectValue placeholder="Selecione uma área" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mais-procurados">Mais Procurados</SelectItem>
              {areas.map(option => (
                <SelectItem key={option.reference} value={option.url}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Tabs>
    </div>
  )
}
